import axios from 'axios';
import {userStore} from './stores/UserStore';
import {snackbarStore} from './stores/SnackbarStore';
import {chargeStore} from './stores/ChargeStore';
import {translate} from './util'
import { apiBaseUrl } from './apiBaseUrl';

const baseURL = apiBaseUrl;

const http = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'X-Picsect-Client': 'web' }
});

let refreshPromise = null;

function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios.post(`${baseURL}/auth/refresh`, {}, {
      withCredentials: true,
      headers: { 'X-Picsect-Client': 'web' }
    }).then(response => {
      const token = response.data?.accessToken || response.data?.token;
      if (!token) throw new Error('Refresh response did not include an access token.');
      userStore.login(token);
      return token;
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export {baseURL}

http.interceptors.request.use((config) => {
  const userRoutesPattern = /^\/user\/.*/;
  if (userRoutesPattern.test(config.url)) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

http.interceptors.response.use(function (response) {
  return response;
}, async function (error) {
  if (error.code === 'ERR_NETWORK') {
    return Promise.reject(error);
  }

  if (!error.response) {
    snackbarStore.showSnackbar(error.message, 'error');
    return Promise.reject(error);
  }

  const { status, data } = error.response;

  const originalRequest = error.config || {};
  const refreshable = status === 401
    && /^\/user\//.test(String(originalRequest.url || ''))
    && !originalRequest._picsectRefreshRetried
    && !String(originalRequest.url || '').includes('/auth/refresh')
    && !String(originalRequest.url || '').includes('/login');
  if (refreshable) {
    originalRequest._picsectRefreshRetried = true;
    try {
      const token = await refreshAccessToken();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return http(originalRequest);
    } catch (refreshError) {
      if ([401, 403].includes(refreshError.response?.status)) {
        userStore.logout({ revoke: false });
      }
      return Promise.reject(refreshError);
    }
  }

  // Authentication state is handled by token refresh or the login flow. Showing the
  // original access-token 401 here is misleading when another concurrent request
  // has already refreshed the session successfully.
  if (status === 401) {
    return Promise.reject(error);
  }

  if (status === 403) {
    if (data?.code === 'USER_BANNED' && error.config?.url !== '/login') {
      userStore.logout({ revoke: false });
      const reason = String(data.reason || '').trim();
      snackbarStore.showSnackbar(reason ? `账号已被封禁：${reason}` : '账号已被封禁，请联系平台客服', 'error');
    }
    return Promise.reject(error);
  }

  if (status === 405 && data?.error === 'Your subscription has expired.') {
    snackbarStore.showSnackbar('订阅已到期，请续费后继续使用', 'error');
    chargeStore.openChargeDialog(false);
    return Promise.reject(error);
  }

  snackbarStore.showSnackbar(translate(data?.error), 'error');
  return Promise.reject(error);
})
export default http;
